import { useEffect, useState } from "react";
import { CheckIcon, SearchIcon, XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { cn } from "@/src/lib/utils";
import { useDebounce } from "@/src/lib/useDebounce";
import { useResizeObserver } from "@/src/lib/useResizeObserver";

export type ComboboxMultiProps = {
  className?: string;
  loadOptions?: (query: string) => Promise<{ value: string; label: string }[]> | { value: string; label: string }[];
  onChange: (value: string[]) => void;
  value: string[];
  placeholder?: string;
  defaultOptions?: { value: string; label: string }[];
};

export function ComboboxMulti<T>(props: ComboboxMultiProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const [items, setItems] = useState<{ value: string; label: string }[]>([]);
  const [width, setWidth] = useState(0);

  const search = useDebounce(async (v: string) => {
    if (props.loadOptions) {
      setItems(await props.loadOptions(v));
    }
  }, 300);
  const onQueryChange = async (v: string) => {
    setQuery(v);
    search(v);
  };

  const triggerRef = useResizeObserver(([e]) => {
    setWidth(e.target.getBoundingClientRect().width);
  });

  useEffect(() => {
    !!props.defaultOptions?.length && setItems(props.defaultOptions);
  }, [props.defaultOptions]);

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <PopoverTrigger className={props.className} asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between bg-white hover:bg-white whitespace-normal text-left")}
        >
          <div className="w-[max-content]">
            <span className="line-clamp-1 break-all">
              {!!props.value?.length
                ? props.value?.map((v, i) => `${v}${props.value?.length > i + 1 ? ", " : ""}`)
                : props.placeholder}
            </span>
          </div>
          {!!props.value?.length && (
            <span className="text-slate-500 ml-auto" onClick={() => props.onChange([])}>
              <XIcon size={20} />
            </span>
          )}
          <SearchIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[200px] p-0", props.className)} style={{ width }}>
        <div className="flex w-full flex-col rounded-md bg-popover text-popover-foreground">
          {props.loadOptions && (
            <div className="flex items-center px-3 border-b">
              <input
                className="flex w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-9"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                role="combobox"
              />
              <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          )}

          {items?.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-popover-foreground">No results</div>
          )}
          {items?.length > 0 && (
            <div
              className="max-h-[300px] p-1 overflow-y-auto overflow-x-hidden"
              role="listbox"
              aria-label="Suggestions"
            >
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`relative flex justify-between cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${
                    props.value && props.value.indexOf(item.value) !== -1 ? "bg-slate-100" : ""
                  }`}
                  role="option"
                  onClick={() => {
                    const valueIndex = props.value.indexOf(item.value);
                    if (!!props.value?.length) {
                      if (valueIndex !== -1) {
                        const values = [...props.value];
                        values.splice(valueIndex, 1);
                        return props.onChange(values);
                      } else {
                        return props.onChange([...props.value, item.value]);
                      }
                    } else {
                      return props.onChange([item.value]);
                    }
                  }}
                >
                  {item.label}
                  {props.value && props.value.indexOf(item.value) !== -1 && <CheckIcon className="ml-2" size={16} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
